import classNames from "classnames";
import { ClipboardEvent, useEffect, useId, useRef, useState } from "react";
import PhoneInput, { Country, getCountries, getCountryCallingCode, parsePhoneNumber, Value } from "react-phone-number-input/input";
import { useShake, vibrateOnError } from "../ErrorFeedback/ErrorFeedback";
import { CountrySelector } from "./CountrySelector";
import { getDefaultCountry, getDialCode } from "./countries";

export const KEYBOARD_SETTLE_DELAY_MS = 300;

export type PhoneNumberInputValue = {
  country: Country;
  dialCode: string;
  number: string;
  isValid: boolean;
};

export type PhoneNumberInputProps = {
  onChange: (value: PhoneNumberInputValue) => void;
  countrySelectorLabel: string;
  numberInputLabel: string;
  placeholder: string;
  hasError?: boolean;
  errorMessageId?: string;
  failedSubmitCount?: number;
  focusOnFailedSubmit?: boolean;
  onErrorReset?: () => void;
  onErrorHaptic?: () => void;
  defaultCountry?: Country;
  defaultValue?: string;
  id?: string;
  name?: string;
};

const countryForCallingCode = (callingCode: string, preferred: Country) =>
  getCountryCallingCode(preferred) === callingCode ? preferred : getCountries().find((country) => getCountryCallingCode(country) === callingCode);

const initialState = (defaultValue: string | undefined, defaultCountry: Country | undefined) => {
  const fallbackCountry = defaultCountry ?? getDefaultCountry();
  const parsed = defaultValue ? parsePhoneNumber(defaultValue, fallbackCountry) : undefined;
  if (parsed) {
    const country = parsed.country ?? countryForCallingCode(parsed.countryCallingCode, fallbackCountry);
    if (country) {
      return { country, value: parsed.number };
    }
  }

  const digits = defaultValue?.replace(/\D/g, "");
  return {
    country: fallbackCountry,
    value: digits ? (`+${getCountryCallingCode(fallbackCountry)}${digits}` as Value) : undefined,
  };
};

export const PhoneNumberInput = ({
  onChange,
  countrySelectorLabel,
  numberInputLabel,
  placeholder,
  hasError = false,
  errorMessageId,
  failedSubmitCount = 0,
  focusOnFailedSubmit = true,
  onErrorReset,
  onErrorHaptic,
  defaultCountry,
  defaultValue,
  id,
  name,
}: PhoneNumberInputProps) => {
  const [initial] = useState(() => initialState(defaultValue, defaultCountry));
  const [country, setCountry] = useState<Country>(initial.country);
  const [value, setValue] = useState<Value | undefined>(initial.value);
  const [isErrorReset, setIsErrorReset] = useState(false);
  const [scope, shake] = useShake();
  const inputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  const onErrorResetRef = useRef(onErrorReset);
  const onErrorHapticRef = useRef(onErrorHaptic);
  const focusOnFailedSubmitRef = useRef(focusOnFailedSubmit);
  const handledSubmitCount = useRef(failedSubmitCount);
  const scrollTimer = useRef<ReturnType<typeof setTimeout>>();
  const prefixId = useId();

  const dialCode = getDialCode(country);
  const parsedValue = value ? parsePhoneNumber(value) : undefined;
  const isValid = !!parsedValue?.isValid() && parsedValue.country === country;
  const showError = hasError && !isErrorReset && !isValid;
  const number = value ? value.slice(dialCode.length) : "";

  onChangeRef.current = onChange;
  onErrorResetRef.current = onErrorReset;
  onErrorHapticRef.current = onErrorHaptic;
  focusOnFailedSubmitRef.current = focusOnFailedSubmit;

  useEffect(() => {
    onChangeRef.current({ country, dialCode, number, isValid });
  }, [country, dialCode, number, isValid]);

  useEffect(() => {
    if (hasError) {
      setIsErrorReset(false);
    }
  }, [hasError]);

  useEffect(() => {
    if (isErrorReset) {
      onErrorResetRef.current?.();
    }
  }, [isErrorReset]);

  useEffect(() => {
    if (failedSubmitCount === handledSubmitCount.current) {
      return;
    }
    handledSubmitCount.current = failedSubmitCount;

    setIsErrorReset(false);
    if (focusOnFailedSubmitRef.current) {
      inputRef.current?.focus();
    }
    (onErrorHapticRef.current ?? vibrateOnError)();
    shake();
  }, [failedSubmitCount, shake]);

  useEffect(() => () => clearTimeout(scrollTimer.current), []);

  const keepInView = () => {
    clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => scope.current?.scrollIntoView?.({ block: "nearest" }), KEYBOARD_SETTLE_DELAY_MS);
  };

  const resetError = () => {
    if (showError) {
      setIsErrorReset(true);
    }
  };

  const handleValueChange = (next?: Value) => {
    resetError();
    setValue(next);
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    resetError();
    const parsed = parsePhoneNumber(e.clipboardData.getData("text"), country);
    if (!parsed?.country || !parsed.isValid()) {
      return;
    }
    e.preventDefault();
    setCountry(parsed.country);
    setValue(parsed.number);
  };

  const handleCountryChange = (next: Country) => {
    resetError();
    setCountry(next);
    setValue(number ? (`${getDialCode(next)}${number}` as Value) : undefined);
  };

  const describedBy = classNames(prefixId, showError && errorMessageId);

  return (
    <div ref={scope} className="flex w-full gap-2">
      <CountrySelector country={country} onChange={handleCountryChange} label={countrySelectorLabel} hasError={showError} />

      <div
        className={classNames(
          "flex h-[58px] min-w-0 flex-1 items-center gap-2 rounded-[10px] bg-l-blue px-5 font-bold text-navy-blue",
          showError ? "outline outline-2 outline-brand-red" : "focus-within:outline focus-within:outline-2 focus-within:outline-navy-blue"
        )}
        data-testid="phone-number-field"
        onPointerDown={resetError}
      >
        <span id={prefixId}>{dialCode}</span>
        <PhoneInput
          ref={inputRef}
          id={id}
          name={name}
          country={country}
          international
          value={value}
          onChange={handleValueChange}
          onPaste={handlePaste}
          onFocus={keepInView}
          inputMode="tel"
          autoComplete="tel"
          aria-label={numberInputLabel}
          aria-invalid={showError}
          aria-describedby={describedBy}
          placeholder={placeholder}
          className={classNames(
            "h-full min-w-0 flex-1 bg-transparent font-bold text-navy-blue",
            "outline-none focus:outline-none placeholder:font-bold placeholder:text-navy-blue-bright"
          )}
        />
      </div>
    </div>
  );
};
